import * as yup from "yup";
import { Form } from "@/components/organisms/Form";
import { request } from "@/common/api";
import { useForm } from "react-hook-form";
import { Buttons } from "@/components/atoms/Buttons";
import { showAlert } from "@/components/containers/Alert";
import { agreeState } from "@/common/states/agree";
import { TelControl } from "@/components/containers/Controls/TelControl";
import { useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { yupResolver } from "@hookform/resolvers/yup";
import { TextControl } from "@/components/containers/Controls/TextControl";
import { BaseResponse } from "@/common/types/baseRespones";
import { FormInputBox } from "@/components/molecules/FormInputBox";
import { JoinTemplate } from "@/components/templates/JoinTemplate";
import { useRecoilValue } from "recoil";
import { JoinProcessBar } from "@/components/atoms/JoinProcessBar";
import { useJoinFormHandlers } from "@/common/hooks/useJoinFormHandlers";
import { GuardianState, joinCommState } from "@/common/states/recoilInfo";

const schema = yup
  .object({
    guardian: yup.object({
      name: yup
        .string()
        .matches(/^[a-zA-Z가-힣]+$/, "이름은 한글 및 영문만 입력 가능합니다.")
        .max(20, "이름은 최대 20자까지 입력 가능합니다.")
        .required("보호자 이름은 필수 입력입니다."),
      birth: yup
        .date()
        .nullable()
        .optional()
        .typeError("생년월일은 유효한 날짜여야 합니다.")
        .required("생년월일은 필수 입력입니다."),
      tel: yup
        .string()
        .length(
          13,
          "연락처는 000-0000-0000 형식이며, 13자에 맞게 작성하셔야 합니다."
        )
        .required("연락처는 필수 입력입니다."),
      agreementTime1: yup
        .date()
        .required("서비스 이용 안내 동의 시간은 필수 입력입니다.")
        .typeError("동의 시간 1은 유효한 날짜여야 합니다."),
      agreementTime2: yup
        .date()
        .required(
          "개인정보 수집∙이용 및 제3자 제공 동의 시간은 필수 입력입니다."
        )
        .typeError("동의 시간 2는 유효한 날짜여야 합니다."),
    }),
  })
  .required("보호자 정보는 필수 입력입니다.");

export const JoinGuardianFormPage = () => {
  const navigate = useNavigate();
  const agree = useRecoilValue(agreeState);
  const joinInfo = useRecoilValue(joinCommState);
  const { guardian, handleNameChange, handleBirthChange, handleNumberChange } =
    useJoinFormHandlers();

  const { control, handleSubmit } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      guardian: {
        tel: guardian.tel,
        name: guardian.name,
        birth: guardian.birth ?? new Date(),
        agreementTime1: agree?.agreementTime1!,
        agreementTime2: agree?.agreementTime2!,
      },
    },
  });

  const { mutateAsync: registerGuardian } = useMutation({
    mutationFn: async (data: { guardian: GuardianState }) => {
      return await request<BaseResponse>({
        method: "POST",
        url: "/guardian/join",
        data: data,
      });
    },
    onSuccess: (response) => {
      navigate("/auth/join/done");
    },
    onError: (error) => {
      showAlert("회원가입 중 오류가 발생하였습니다.");
    },
  });

  const onSubmit = async (data: any) => {
    const guardianData: GuardianState = {
      name: data.guardian.name,
      birth: data.guardian.birth,
      tel: data.guardian.tel,
      userId: joinInfo.userId,
      password: joinInfo.password,
      questionAnswer: joinInfo.questionAnswer,
      accountFindQuestion: joinInfo.accountFindQuestion,
      agreementTime1: data.guardian.agreementTime1,
      agreementTime2: data.guardian.agreementTime2,
    };

    try {
      await registerGuardian({ guardian: guardianData });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <JoinTemplate>
      <Form
        processBar={<JoinProcessBar stepNow={5} stepTotal={5} />}
        title="법정보호자 회원 정보 입력"
        submitBtn={
          <Buttons
            type="submit"
            size="lg"
            color="primary100"
            layout="solid"
            label="회원가입"
            disabled={false}
            onClick={handleSubmit(onSubmit)}
          />
        }
        backBtn={
          <Buttons
            type="button"
            size="lg"
            color="primary100"
            layout="text"
            label="뒤로"
            onClick={() => navigate("/auth/join/account-form")}
          />
        }
      >
        <FormInputBox formTitle="회원정보 입력">
          <>
            <TextControl
              control={control}
              name="guardian.name"
              label="이름"
              placeholder="이름을 입력하세요."
              required={true}
              value={guardian.name}
              onChange={(e) => handleNameChange(e, "guardian")}
              instructions="name"
            />
            <TextControl
              type="date"
              control={control}
              name="guardian.birth"
              label="생년월일"
              placeholder="0000-00-00"
              required={true}
              value={
                guardian.birth ? guardian.birth.toISOString().split("T")[0] : ""
              }
              onChange={(e) => handleBirthChange(e, "guardian")}
              instructions="birth"
            />
            <TelControl
              type="tel"
              control={control}
              name="guardian.tel"
              label="연락처"
              placeholder="000-0000-0000"
              required={true}
              value={guardian.tel}
              onChange={(e) => handleNumberChange(e, "guardian")}
            />
          </>
        </FormInputBox>
      </Form>
    </JoinTemplate>
  );
};
